<template>
	<div class="flex flex-col mb-4 "
			 :class="{ 'select-none opacity-50': disabled ?? schema.disabled, 'hidden': resolveType() == 'hidden' }"
			 :id="undefined"
			 v-if="isNativeInput && schema">
		<label
			class="block text-neutral-700 text-sm font-bold dark:text-neutral-300 !inline-flex mb-1.5 formkit-label">
			{{ schema.label }}<span v-if="isRequired" class="text-red-500">✱</span>
		</label>
		<template v-if="schema.$formkit == 'textarea'">
			<AppTextareaInput
				v-model="inputValue"
				:id="id ?? schema.id"
				:name="schema.name"
				:disabled="disabled ?? schema.disabled"
				:input-class="inputClass"
				@change="updateValue"
			/>
		</template>
		<template v-else-if="schema.$formkit == 'datepicker'">
			<AppDateInput
				v-model="inputValue"
				:id="id ?? schema.id"
				@change="updateValue"
			/>
		</template>
		<template v-else-if="resolveType() == 'number'">
			<AppNumberInput
				v-model="inputValue"
				:step="schema.step"
				:name="schema.name"
				:id="id ?? schema.id"
				:disabled="disabled ?? schema.disabled"
				:invalid="errors.length > 0"
				:prefix="prefix"
				:suffix="suffix"
				:input-class="inputClass"
				:wrapper-class="wrapperClass"
				:live-debounce="liveDebounce"
				@change="onNumberInputChange"
				@keyup="emit('keyup', $event, node)"
			>
				<template #prefix v-if="$slots.prefix">
					<slot name="prefix" />
				</template>
				<template #suffix v-if="$slots.suffix">
					<slot name="suffix" />
				</template>
			</AppNumberInput>
		</template>
		<template v-else>
			<div
				class="flex items-center rounded border border-neutral-400 focus-within:ring-1 focus-within:!ring-primary focus-within:!border-primary"
				:class="{ '!border-red-500 !ring-red-500': errors.length > 0, [wrapperClass ?? '']: true }">
				<slot name="prefix"><span v-if="prefix" class="pl-3 opacity-60 select-none whitespace-nowrap">{{ prefix
					}}</span></slot>
				<input
					class="focus:outline-none text-base flex items-center w-full py-2.5 px-3.5 bg-white rounded"
					:class="{ 'bg-disabled': disabled ?? schema.disabled, [inputClass ?? '']: true }"
					:type="resolveType()"
					v-model="inputValue"
					:name="schema.name"
					:id="id ?? schema.id"
					@input="updateValue"
					@change="updateValue"
					:disabled="disabled ?? schema.disabled"
					:data-disabled="disabled ?? schema.disabled"
					v-bind="inputProps"
				>
				<slot name="suffix"><span v-if="suffix" class="pr-3 opacity-60 select-none whitespace-nowrap">{{ suffix
					}}</span></slot>
			</div>
		</template>
		<div v-if="$slots.help">
			<slot name="help"/>
		</div>
		<div v-else-if="helpText">
			<p class="text-xs ml-2 text-gray-500 mt-1">{{ helpText }}</p>
		</div>
		<div v-for="error in errors">
			<p class="text-red-500 text-xs mb-2">{{ error }}</p>
		</div>
	</div>
	<template v-else>
		<FormKitSchema
			v-bind="{ ...$attrs, validation }"
			:id="id ?? schema.id"
			:schema="schema"
			:data="data"
			:sections-schema="sectionsSchema"
			:outer-class="$attrs.class"
			:help="helpText || undefined"
			@node="
      (n) => {
        node = n;

        emit('ready', node);

        n.on('commit', (e: any) => onChange(e.payload, n));
        n.on('prop', (e: any) => {
          if (e.payload.prop == '_isFocused' && e.payload.value == true) {
            emit('focus', node);
          } else if (
            e.payload.prop == '_isFocused' &&
            e.payload.value == true
          ) {
            emit('blur', node);
          }
        });
      }
    "
			@keyup="emit('keyup', $event, node)"
		>
			<slot />
		</FormKitSchema>
	</template>
</template>

<script setup lang="ts">
import { createMessage, FormKitNode, FormKitSchemaDefinition } from "@formkit/core";
import { FormKitComponentLibrary, FormKitSchema } from "@formkit/vue";
import { inject, Ref, toRaw } from "vue";
import AppDateInput from "./AppDateInput.vue";
import AppTextareaInput from "./AppTextareaInput.vue";

const node = ref(null as FormKitNode | null);

const props = withDefaults(
	defineProps<{
		id?: string;
		native?: boolean;
		wrapperClass?: string;
		inputClass?: string;
		schema: Omit<FormKitSchemaDefinition, string>;
		validation?: string | string[];
		data?: Record<string, any>;
		library?: FormKitComponentLibrary;
		memoKey?: string;
		prefix?: string;
		suffix?: string;
		sectionsSchema?: any;
		unit?: string;
		disabled?: boolean;
		indeterminateWhenNull?: boolean;
		help?: string | boolean;
		// Forwarded to AppNumberInput: commit while typing after this many ms of idle instead of
		// only on blur/Enter. Deliberately not named `debounce`, which is passed through $attrs to
		// FormKit's own autocomplete debounce.
		liveDebounce?: number;
	}>(),
	{
		disabled: undefined,
		native: true,
		data: () => ({}),
		library: () => ({}),
		indeterminateWhenNull: false,
	},
);

const schema = toRef(props, "schema");

const rootFormNode: Ref<FormKitNode> = inject("formNode");

// The live form model and field map, provided by FormPage. Absent when this input is used outside
// a form — the print modals do that — in which case we keep binding to the schema node as before.
const formValues = inject<Ref<Record<string, any>> | null>("formValues", null);
const formSchema = inject<Ref<Record<string, any>> | null>("formSchema", null);

/**
 * Whether this input renders a *top-level* field of the surrounding form. Compared by object
 * identity rather than by name: a group or repeater child is a different object than the form's
 * own entry for that name, so nested inputs correctly fall back to the schema node.
 *
 * Both sides go through `toRaw` because one may be a reactive proxy of the other — the field map
 * is built from a reactive array, while the node arrives here as a prop — and `===` between a
 * proxy and its target is false.
 */
const isFormField = computed(() => {
	if (!formValues || !formSchema || !schema.value?.name) return false;

	return toRaw(formSchema.value?.[schema.value.name]) === toRaw(schema.value);
});

/**
 * The one source of truth for this input's value.
 *
 * For a form field that is the form model itself, so a programmatic change anywhere — AppQuantities
 * recalculating, a grid lookup filling sibling fields, a 428 retry — reaches the input, and an edit
 * here reaches the model. Neither direction needs a copy kept in sync, which is what the write-back
 * in FormPage used to do by mutating this component's `schema` prop.
 */
const inputValue = computed({
	get: () => (isFormField.value ? formValues!.value?.[schema.value.name!] : schema.value?.value),
	set: (value) => {
		if (isFormField.value) {
			formValues!.value[schema.value.name!] = value;
		} else {
			schema.value.value = value;
		}
	},
});

const oldValue = ref(inputValue.value);

watchOnce(
	rootFormNode,
	() => {
		if (!rootFormNode || rootFormNode.value == null) return;

		rootFormNode.value.on("field-constraint-violation", (e) => {
			if (e.payload.field !== schema.value.name) return;

			errors.value.push(e.payload.message);
		});

		rootFormNode.value.on("pre-submit", () => {
			validate(inputValue.value);
		});
	},
	{ immediate: true },
);

watch(inputValue, (newValue) => {
	if (oldValue.value !== newValue) {
		validate(newValue);
	}

	oldValue.value = newValue;
});

// Opt-in tri-state: render the checkbox as indeterminate (dash) while its value is null.
// Only touches the native DOM property when explicitly enabled and for checkbox fields.
function applyIndeterminate() {
	if (!props.indeterminateWhenNull) return;
	if (schema.value?.$formkit !== "checkbox") return;

	const el = document.getElementById(props.id ?? schema.value.id) as HTMLInputElement | null;
	if (el) el.indeterminate = inputValue.value == null;
}

watch([node, () => props.indeterminateWhenNull, inputValue], () => nextTick(applyIndeterminate), {
	immediate: true,
});

const isNativeInput = computed(() => {
	if (!props.native) return false;
	if (props.schema == null) return true;

	return ![
		"autocomplete",
		"colorpicker",
		"dropdown",
		"checkbox",
		"group",
		"repeater",
		"list",
		"taglist",
		"togglebuttons",
	].includes(props.schema.$formkit);
});

const validation = computed(() => {
	if (schema.value == null) return false;

	return [props.validation, schema.value.validation]
		.filter((it) => it != null)
		.flat()
		.join("|");
});

const isRequired = computed(() => {
	if (schema.value == null) return false;
	if (!validation.value) return false;

	return validation.value.includes("required");
});

const helpText = computed(() => {
	if (schema.value == null) return;
	// `help` allows a Boolean type, so Vue casts an *absent* prop to `false` (not undefined), and some
	// callers pass `false` to mean "no help". Either way a non-string value must not shadow the field's
	// own help — only an explicit string overrides; otherwise fall back to the schema's `help`.
	return (typeof props.help === "string" ? props.help : undefined) ?? schema.value.help;
});

const sectionsSchema = ref(props.sectionsSchema);

if (props.suffix) {
	if (sectionsSchema.value == undefined) {
		sectionsSchema.value = {};
	}

	sectionsSchema.value["suffix"] = {
		$el: "span",
		attrs: {
			class: "pr-3 opacity-60 select-none",
		},
		children: props.suffix,
	};
}

const emit = defineEmits<{
	(e: "change", value: any | null, node: FormKitNode): void;
	(e: "keyup", event: KeyboardEvent, node: FormKitNode): void;
	(e: "ready", node: FormKitNode): void;
	(e: "focus", node: FormKitNode): void;
	(e: "blur", node: FormKitNode): void;
}>();

function resolveType() {
	if (props.schema.$formkit == "datepicker") {
		return "date";
	}

	return props.schema.$formkit;
}

const inputProps = computed(() => {
	const props = {} as Record<string, any>;

	if (schema.disabled) {
		props["data-disabled"] = true;
	}

	if (errors.value.length > 0) {
		props["data-invalid"] = true;
	}

	if (resolveType() == "number") {
		props["step"] = schema.value.step;
	}

	return props;
});

function updateValue() {
	if (!rootFormNode) return;

	validate(inputValue.value);

	// A form field is already written straight into the model by the setter above. The event is
	// what carries the fallback path — nested nodes and inputs outside a form — up to FormPage.
	if (!isFormField.value) {
		rootFormNode.value.emit("update:property", { name: schema.value.name, value: inputValue.value });
	}
}

// AppNumberInput commits its value (and emits `change`) on blur/Enter, and — when `liveDebounce`
// is set — while typing too, in both cases after the model is set. Run the FormKit update first,
// then forward the committed value upward so hosts like AppQuantities can recalculate off a fresh
// value.
function onNumberInputChange(value: number | null) {
	updateValue();
	emit("change", value, node.value as FormKitNode);
}

const errors = ref([] as string[]);

function parseValidationString(input: string) {
	if (input.trim() === "") {
		return [];
	}

	return input.split("|").map((rule) => {
		const [name, args] = rule.split(":");
		return args ? [name, ...args.split(",")] : [name];
	});
}

function addError(message: string) {
	errors.value.push(message);
	rootFormNode.value.store.set(
		createMessage({
			key: schema.value.id,
			type: "error",
			blocking: true,
			value: message,
		}),
	);
}

function validate(newValue: any) {
	if (!isNativeInput.value || !rootFormNode) return;

	rootFormNode.value.store.remove(schema.value.id);

	if (!validation.value) return;

	const validations = parseValidationString(validation.value);
	const label = schema.value.label;

	errors.value = [];
	validations.forEach(([rule, ...args]) => {
		if (
			rule == "length" &&
			resolveType() == "text" &&
			newValue != null &&
			newValue !== "" &&
			(newValue.length < args[0] || newValue.length > args[1])
		) {
			addError(`${label} moet tussen de ${args[0]} en ${args[1]} karakters lang zijn.`);
		}

		if (rule == "min" && resolveType() == "number" && Number(newValue) < Number(args[0])) {
			addError(`${label} moet minimaal ${args[0]}${props.unit ?? ""} zijn.`);
		}

		if (rule == "max" && resolveType() == "number" && Number(newValue) > Number(args[0])) {
			addError(`${label} mag maximaal ${args[0]}${props.unit ?? ""} zijn.`);
		}

		if (rule == "notBlank" && resolveType() == "text" && newValue == "") {
			addError(`${label} mag niet leeg zijn.`);
		}

		if (rule == "required" && (newValue == null || newValue === "" || newValue == undefined)) {
			addError(`${label} is verplicht!`);
		}
	});
}

function onChange(payload: any, node: FormKitNode) {
	// Have to apply a slight delay because Formkit is badly designed.
	setTimeout(() => {
		emit("change", payload, node);
	}, 100);
}
</script>

<style>
.bg-disabled {
	@apply cursor-not-allowed;
	background-color: rgb(245 245 245 / var(--tw-bg-opacity, 1));
}
</style>
